import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Key, Plus, Trash2, Copy, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { copyToClipboard, formatDate } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { SettingsLayout } from '@/client/components/layout/settings-layout';

interface ApiToken {
  id: string;
  name: string;
  tokenPrefix: string;
  createdAt: string;
}

export function ApiTokensPage() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['tokens'],
    queryFn: async () => {
      const response = await apiFetch('/api/tokens');
      if (!response.ok) throw new Error('Failed to fetch tokens');
      return response.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      const response = await apiFetch('/api/tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, scopes: ['links:create', 'links:read'] }),
      });
      if (!response.ok) throw new Error('Failed to create token');
      return response.json();
    },
    onSuccess: (data) => {
      setNewToken(data.token.tokenValue);
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
      toast.success('Token created!');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (tokenId: string) => {
      const response = await apiFetch(`/api/tokens/${tokenId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete token');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
      toast.success('Token deleted');
    },
  });

  const handleCopyToken = async (token: string) => {
    await copyToClipboard(token);
    toast.success('Token copied!');
  };

  return (
    <SettingsLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">API Tokens</h2>
          <p className="text-sm text-muted-foreground">Manage tokens for API access.</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          <Plus className="h-4 w-4" />
          Create Token
        </button>
      </div>

      {/* New Token Alert */}
      {newToken && (
        <div className="rounded-lg border border-green-500 bg-green-50 p-4">
          <p className="font-medium text-green-800 mb-2">
            Your new API token (copy it now, you won&apos;t see it again):
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-background px-3 py-2 rounded border text-sm font-mono">
              {showToken ? newToken : '•'.repeat(40)}
            </code>
            <button
              onClick={() => setShowToken(!showToken)}
              className="p-2 rounded-md hover:bg-background"
            >
              {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <button
              onClick={() => handleCopyToken(newToken)}
              className="p-2 rounded-md hover:bg-background"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={() => setNewToken(null)}
            className="mt-2 text-sm text-green-700 hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Tokens List */}
      <div className="rounded-lg border bg-card">
        {isLoading ? (
          <div className="p-6 animate-pulse space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-12 bg-muted rounded" />
            ))}
          </div>
        ) : data?.tokens?.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No API tokens yet.</p>
          </div>
        ) : (
          <div className="divide-y">
            {data?.tokens?.map((token: ApiToken) => (
              <div key={token.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">{token.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {token.tokenPrefix}••• · Created {formatDate(token.createdAt)}
                  </p>
                </div>
                <button
                  onClick={() => deleteMutation.mutate(token.id)}
                  className="p-2 rounded-md hover:bg-destructive/10 text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg shadow-lg max-w-md w-full p-6">
            <h2 className="text-lg font-semibold mb-4">Create API Token</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                createMutation.mutate(formData.get('name') as string);
                setShowCreateModal(false);
              }}
            >
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Token Name</label>
                <input
                  name="name"
                  type="text"
                  required
                  placeholder="e.g., My Website"
                  className="w-full px-4 py-2 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 rounded-md border hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SettingsLayout>
  );
}
